/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
  DOMConversionMap,
  DOMConversionOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";
import { $applyNodeReplacement, DecoratorNode, DOMExportOutput } from "lexical";
import { F } from "ramda";
import React, { JSX, Suspense } from "react";

import { PlaceholderComponent } from "./PlaceholderComponent";

export type SerializedPlaceholderNode = Spread<
  {
    placeholder: string;
  },
  SerializedLexicalNode
>;

function $convertPlaceholderElement(domNode: HTMLElement): null | DOMConversionOutput {
  let placeholder = domNode.getAttribute("data-lexical-placeholder");
  // Decode the placeholder from base64
  placeholder = atob(placeholder || "");
  if (placeholder) {
    const node = $createPlaceholderNode(placeholder);
    return { node };
  }

  return null;
}

export class PlaceholderNode extends DecoratorNode<JSX.Element> {
  __placeholder: string;

  static getType(): string {
    return "sinchPlaceholder";
  }

  static clone(node: PlaceholderNode): PlaceholderNode {
    return new PlaceholderNode(node.__placeholder, node.__key);
  }

  constructor(placeholder: string, key?: NodeKey) {
    super(key);
    this.__placeholder = placeholder;
  }

  static importJSON(serializedNode: SerializedPlaceholderNode): PlaceholderNode {
    return $createPlaceholderNode(serializedNode.placeholder).updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedPlaceholderNode {
    return {
      ...super.exportJSON(),
      placeholder: this.getPlaceholder(),
    };
  }

  createDOM(): HTMLElement {
    const element = document.createElement("span");
    // PlaceholderNodes should implement `user-action:none` in their CSS to avoid issues with deletion on Android.
    element.className = "editor-placeholder";
    return element;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("span");
    // Encode the placeholder as base64 to avoid issues with special characters
    const placeholder = btoa(this.__placeholder);
    element.setAttribute("data-lexical-placeholder", placeholder);
    element.innerText = "[[" + this.__placeholder + "]]";
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute("data-lexical-placeholder")) {
          return null;
        }
        return {
          conversion: $convertPlaceholderElement,
          priority: 2,
        };
      },
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute("data-lexical-placeholder")) {
          return null;
        }
        return {
          conversion: $convertPlaceholderElement,
          priority: 1,
        };
      },
    };
  }

  updateDOM = F;

  getTextContent(): string {
    return `[[${this.__placeholder}]]`;
  }

  getPlaceholder(): string {
    return this.__placeholder;
  }

  setPlaceholder(placeholder: string): void {
    const writable = this.getWritable();
    writable.__placeholder = placeholder;
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <PlaceholderComponent nodeKey={this.__key} placeholder={this.__placeholder} />
      </Suspense>
    );
  }
}

export function $createPlaceholderNode(placeholder = ""): PlaceholderNode {
  const placeholderNode = new PlaceholderNode(placeholder);
  return $applyNodeReplacement(placeholderNode);
}

export function $isPlaceholderNode(node: LexicalNode | null | undefined): node is PlaceholderNode {
  return node instanceof PlaceholderNode;
}
